import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Users, MessageCircle, ChevronLeft, Trash2, Loader2, FileText, ArrowLeft, Download, Volume2, Zap } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function SessionHistory({ ReportStage, personaChat, moderatorChat }) {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/sessions?status=completed`);
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const openReport = async (session) => {
    setSelectedSession(session);
    setLoadingReport(true);
    try {
      const res = await fetch(`${API_BASE}/sessions/${session.id}/report`);
      if (res.ok) {
        const data = await res.json();
        setReportData(data);
      }
    } catch (err) {
      console.error('Failed to fetch report:', err);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDelete = async (sessionId) => {
    try {
      const res = await fetch(`${API_BASE}/sessions/${sessionId}`, { method: 'DELETE' });
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        setDeleteConfirm(null);
        if (selectedSession?.id === sessionId) {
          setSelectedSession(null);
          setReportData(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const backToList = () => {
    setSelectedSession(null);
    setReportData(null);
  };

  const exportPDF = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE}/report/${sessionId}/pdf`);
      if (!response.ok) throw new Error('Failed to export PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('export.pdfFilename')}_${sessionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

  const exportDOCX = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE}/report/${sessionId}/docx`);
      if (response.status === 501) return;
      if (!response.ok) throw new Error('Failed to export DOCX');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('export.docxFilename')}_${sessionId}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('DOCX export failed:', err);
    }
  };

  const exportAudio = async (sessionId) => {
    try {
      const estResponse = await fetch(`${API_BASE}/report/${sessionId}/audio/estimate`);
      if (!estResponse.ok) throw new Error('Failed to get audio estimate');
      const estimate = await estResponse.json();

      const confirmed = window.confirm(
        t('export.audioConfirm')
          .replace('{characters}', estimate.total_characters.toLocaleString())
          .replace('{cost}', estimate.estimated_cost_usd.toFixed(2))
          .replace('{duration}', estimate.estimated_duration_minutes)
      );
      if (!confirmed) return;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000);
      const response = await fetch(`${API_BASE}/report/${sessionId}/audio`, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.status === 501) return;
      if (!response.ok) throw new Error('Failed to export audio');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('export.audioFilename')}_${sessionId}.mp3`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Audio export failed:', err);
    }
  };

  // Show full report
  if (selectedSession && reportData) {
    const config = {
      title: reportData.title || '',
      topic: reportData.topic || '',
      description: reportData.description || '',
      questions: reportData.questions || [],
      moderatorBriefing: reportData.moderator_briefing || '',
      rounds: reportData.round_summaries?.length || 0,
      participantCount: reportData.personas?.length || 0,
    };

    const personas = (reportData.personas || []).map(p => ({
      id: p.id,
      name: p.name,
      age: p.age,
      gender: p.gender,
      region: p.region,
      employment: p.employment,
      education: p.education,
      family_status: p.family_status,
      personality_traits: p.personality_traits || p.traits || [],
      background_story: p.background_story || p.background || '',
      model_id: p.model_id || '',
      provider: p.provider || '',
    }));

    const messages = (reportData.messages || []).map(m => ({
      id: m.id,
      speaker: m.speaker,
      speakerId: m.speaker_id,
      content: m.content,
      round: m.round,
      timestamp: m.timestamp || m.created_at,
      keyPoints: m.key_points ? (typeof m.key_points === 'string' ? JSON.parse(m.key_points) : m.key_points) : [],
      sentiment: m.sentiment,
    }));

    const roundSummaries = (reportData.round_summaries || []).map(rs => ({
      round: rs.round,
      key_themes: rs.key_themes ? (typeof rs.key_themes === 'string' ? JSON.parse(rs.key_themes) : rs.key_themes) : [],
      tensions: rs.tensions ? (typeof rs.tensions === 'string' ? JSON.parse(rs.tensions) : rs.tensions) : [],
      consensus_points: rs.consensus_points ? (typeof rs.consensus_points === 'string' ? JSON.parse(rs.consensus_points) : rs.consensus_points) : [],
      sentiment_shift: rs.sentiment_shift || 0,
    }));

    const finalAnalysis = reportData.final_analysis
      ? (typeof reportData.final_analysis === 'string' ? JSON.parse(reportData.final_analysis) : reportData.final_analysis)
      : null;

    const beliefStates = reportData.belief_states || {};

    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={backToList}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('history.backToList')}
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => exportPDF(selectedSession.id)}
              className="btn btn-primary flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              PDF
            </button>
            <button
              onClick={() => exportDOCX(selectedSession.id)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Word
            </button>
            <button
              onClick={() => exportAudio(selectedSession.id)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Volume2 className="w-4 h-4" />
              MP3
            </button>
          </div>
        </div>
        {reportData.stimulus_text && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">
                {t('stimulus.referenceLabel')}{reportData.stimulus_label ? ` — ${reportData.stimulus_label}` : ''}
              </span>
            </div>
            <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">{reportData.stimulus_text}</p>
          </div>
        )}
        <ReportStage
          config={config}
          personas={personas}
          messages={messages}
          roundSummaries={roundSummaries}
          finalAnalysis={finalAnalysis}
          moderatorSummary={reportData.moderator_summary || ''}
          beliefStates={beliefStates}
          sessionId={selectedSession.id}
          personaChat={personaChat}
          moderatorChat={moderatorChat}
        />
      </div>
    );
  }

  // Loading report spinner
  if (selectedSession && loadingReport) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-4" />
        <p className="text-stone-500">{t('history.loadingReport')}</p>
      </div>
    );
  }

  // Session list
  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-serif font-bold text-stone-800">{t('history.title')}</h2>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-4" />
          <p className="text-stone-500">{t('history.loading')}</p>
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-100 rounded-full mb-4">
            <Clock className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="text-lg font-medium text-stone-600 mb-2">{t('history.noSessions')}</h3>
          <p className="text-stone-400">{t('history.noSessionsDesc')}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {sessions.map((session) => (
            <motion.div
              key={session.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-stone-800 text-lg truncate">
                      {session.title || session.topic || 'Untitled session'}
                    </h3>
                    {session.stimulus_text && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex-shrink-0">
                        <Zap className="w-3 h-3" />
                        {t('history.stimulusBadge')}
                      </span>
                    )}
                  </div>
                  {session.topic && session.title && (
                    <p className="text-stone-500 text-sm mt-1 truncate">{session.topic}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-sm text-stone-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {session.created_at ? new Date(session.created_at).toLocaleDateString() : '—'}
                    </span>
                    {session.participant_count && (
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {session.participant_count} {t('history.participants').toLowerCase()}
                      </span>
                    )}
                    {session.rounds && (
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3.5 h-3.5" />
                        {session.rounds} {t('history.rounds').toLowerCase()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => openReport(session)}
                    className="btn btn-primary text-sm px-4 py-2"
                  >
                    {t('history.openReport')}
                  </button>

                  {deleteConfirm === session.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(session.id)}
                        className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                      >
                        {t('history.deleteSession')}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="text-xs px-2 py-1 bg-stone-200 text-stone-600 rounded hover:bg-stone-300 transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(session.id)}
                      className="p-2 text-stone-300 hover:text-red-500 transition-colors"
                      title={t('history.deleteSession')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
